import { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import { validationOrderRegistration } from '../../../util/validation';
import { useProductStore } from '../../../store/useProduct';
import { usePageAlertStore } from '../../../store/usePageAlert';
import * as XLSX from 'xlsx';
import { mapHeadersToEnglish, removeBracketContent } from '../../../util/util';
import { IOrderRegistration } from '../../../type/type';
import { useOrderStore } from '../../../store/useOrder';
import { useFranchiseStore } from '../../../store/useFranchise';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUpload,
  faDownload,
  faCheckCircle,
  faExclamationCircle,
  faSpinner,
} from '@fortawesome/free-solid-svg-icons';

/**
 * @author taulmax
 * @date 2024.10.07
 * @description 주문 등록 페이지 - 엑셀 스타일 UI/UX로 리뉴얼
 */
const OrderRegistration = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  const [editingCell, setEditingCell] = useState<{
    row: number;
    field: string;
  } | null>(null);
  const [selectedCell, setSelectedCell] = useState<{
    row: number;
    field: string;
  } | null>(null);
  const [tempValue, setTempValue] = useState<string>('');
  const tableRef = useRef<HTMLTableElement>(null);

  const [files, setFiles] = useState<File[]>([]);
  const [excelData, setExcelData] = useState<IOrderRegistration[]>([]);
  const [validationResult, setValidationResult] = useState<any>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [showRealtimeNotice, setShowRealtimeNotice] = useState(false);
  const [noticeSecondsLeft, setNoticeSecondsLeft] = useState(0);

  // 최신 데이터를 참조하기 위한 ref
  const excelDataRef = useRef<IOrderRegistration[]>(excelData);
  const selectedRowsRef = useRef<Set<number>>(selectedRows);
  useEffect(() => {
    excelDataRef.current = excelData;
  }, [excelData]);
  useEffect(() => {
    selectedRowsRef.current = selectedRows;
  }, [selectedRows]);

  const { products, getProducts } = useProductStore();
  const { postOrder } = useOrderStore();
  const { myfranchise, getMyFranchise } = useFranchiseStore();
  const { setAlert, setButton, setSecondaryButton, clearButton, clearSecondaryButton, clearAll } = usePageAlertStore();

  // 비즈 프로필 활성화 여부 체크
  const isBizProfileEnabled = useMemo(
    () => Boolean(myfranchise?.bizmProfileKey),
    [myfranchise?.bizmProfileKey]
  );

  // 테이블 컬럼 정의 - useMemo로 감싸서 매 렌더마다 새로 생성되지 않도록 함
  const columns = useMemo(
    () => [
      { field: 'sequence', header: '순번', width: '60px' },
      { field: 'productCodeOrName', header: '상품명', width: '100px' },
      (isBizProfileEnabled ? { field: 'telNo', header: '연락처', width: '120px' }: null),
      { field: 'email', header: '이메일', width: '180px' },
      { field: 'name1', header: '이름1', width: '80px' },
      { field: 'solarType1', header: '양/음력1', width: '80px' },
      { field: 'year1', header: '생년1', width: '60px' },
      { field: 'month1', header: '생월1', width: '60px' },
      { field: 'day1', header: '생일1', width: '60px' },
      { field: 'hour1', header: '생시1', width: '60px' },
      { field: 'minute1', header: '생분1', width: '60px' },
      { field: 'sex1', header: '성별1', width: '60px' },
      { field: 'name2', header: '이름2', width: '80px' },
      { field: 'solarType2', header: '양/음력2', width: '80px' },
      { field: 'year2', header: '생년2', width: '60px' },
      { field: 'month2', header: '생월2', width: '60px' },
      { field: 'day2', header: '생일2', width: '60px' },
      { field: 'hour2', header: '생시2', width: '60px' },
      { field: 'minute2', header: '생분1', width: '60px' },
      { field: 'sex2', header: '성별2', width: '60px' },
    ].filter(Boolean),
    [isBizProfileEnabled]
  );

  useEffect(() => {
    getProducts();
    getMyFranchise();
  }, [getProducts, getMyFranchise]);

  useEffect(() => {
    if (excelData.length > 0 && products) {
      const productData = products.map(product => ({
        productCode: product.productCode,
        productName: product.productName,
        custInfoCount: product.custInfoCount,
      }));

      const validation = validationOrderRegistration(excelData, productData);
      setValidationResult(validation);
    }
  }, [excelData, products]);

  // validationResult가 변경될 때 Title에 알림 및 버튼 설정
  useEffect(() => {
    if (!validationResult || excelData.length === 0) {
      clearAll();
      return;
    }

    const errorCount = Object.keys(validationResult).length;

    if (errorCount === 0) {
      setAlert('success', '입력 내용에 이상이 없습니다', `총 ${excelData.length}건`);
      // 전체 일괄 등록 버튼 설정
      setButton('전체 일괄 등록', () => {
        const event = new CustomEvent('orderBulkSubmit');
        window.dispatchEvent(event);
      });
      // 실시간 등록 버튼 설정
      const realtimeLabel = selectedRows.size > 0
        ? `선택 항목 실시간 등록 (${selectedRows.size}건)`
        : '선택 항목 실시간 등록';
      setSecondaryButton(
        realtimeLabel,
        () => {
          const event = new CustomEvent('orderRealtimeSubmit');
          window.dispatchEvent(event);
        },
        selectedRows.size === 0 // disabled 상태
      );
    } else {
      clearButton();
      clearSecondaryButton();

      const firstErrorRow = Object.keys(validationResult).map(Number).sort((a, b) => a - b)[0];
      const firstErrors = validationResult[firstErrorRow];
      const firstErrorField = Object.keys(firstErrors)[0];
      const firstErrorMessage = firstErrors[firstErrorField];
      const restCount = errorCount - 1;

      setAlert(
        'error',
        `${firstErrorRow + 1}행: ${firstErrorMessage}`,
        restCount > 0 ? `외 ${restCount}건` : undefined,
        () => {
          setSelectedCell({ row: firstErrorRow, field: firstErrorField });
          setTimeout(() => {
            const cellId = `cell-${firstErrorRow}-${firstErrorField}`;
            const cellElement = document.getElementById(cellId);
            if (cellElement) {
              cellElement.scrollIntoView({ behavior: 'auto', block: 'center', inline: 'center' });
            }
          }, 50);
        }
      );
    }
  }, [validationResult, excelData.length, selectedRows.size, setAlert, setButton, setSecondaryButton, clearButton, clearSecondaryButton, clearAll]);

  // 커스텀 이벤트 리스너 - 헤더 버튼 클릭시 일괄/실시간 등록 호출
  useEffect(() => {
    const tempObj: Record<string, string> = {
      양력: 'SOLAR',
      음력: 'LUNAR',
      '음력(윤달)': 'LUNAR_INTER',
      남성: 'M',
      여성: 'F',
    };

    const handleBulkEvent = () => {
      if (editingCell) {
        toast.warn('수정을 완료하고 등록해주세요.');
        return;
      }
      const currentData = excelDataRef.current;
      const tempExcelData = currentData.map((data: IOrderRegistration) => {
        const newObj: any = { ...data, realtimeYn: 'N' };
        newObj.solarType1 = tempObj[data.solarType1];
        newObj.sex1 = tempObj[data.sex1];
        if (data.solarType2) newObj.solarType2 = tempObj[data.solarType2];
        if (data.sex2) newObj.sex2 = tempObj[data.sex2];
        return newObj;
      });
      postOrder(tempExcelData, navigate);
    };

    const handleRealtimeEvent = async () => {
      if (editingCell) {
        toast.warn('수정을 완료하고 등록해주세요.');
        return;
      }
      const currentData = excelDataRef.current;
      const currentSelectedRows = selectedRowsRef.current;
      const selectedData = currentData.filter((_, i) => currentSelectedRows.has(i));
      if (selectedData.length === 0) {
        toast.warn('선택된 주문이 없습니다.');
        return;
      }
      const newSelectedRows = selectedData.map(row => {
        const newObj: any = { ...row, realtimeYn: 'Y' };
        newObj.solarType1 = tempObj[row.solarType1];
        newObj.sex1 = tempObj[row.sex1];
        if (row.solarType2) newObj.solarType2 = tempObj[row.solarType2];
        if (row.sex2) newObj.sex2 = tempObj[row.sex2];
        return newObj;
      });
      try {
        await postOrder(newSelectedRows);
        setExcelData(prevData => prevData.filter((_, i) => !currentSelectedRows.has(i)));
        setSelectedRows(new Set());
        if (currentData.length === selectedData.length) {
          navigate('/order/list');
        } else {
          setShowRealtimeNotice(true);
        }
      } catch (error) {
        console.error(error);
      }
    };

    window.addEventListener('orderBulkSubmit', handleBulkEvent);
    window.addEventListener('orderRealtimeSubmit', handleRealtimeEvent);
    return () => {
      window.removeEventListener('orderBulkSubmit', handleBulkEvent);
      window.removeEventListener('orderRealtimeSubmit', handleRealtimeEvent);
    };
  }, [editingCell, navigate, postOrder]);

  // 페이지 이탈 시 알림 지우기
  useEffect(() => {
    return () => {
      clearAll();
    };
  }, [clearAll]);

  // 파일 처리 공통 함수
  const processFile = useCallback((file: File) => {
    if (!file.name.endsWith('.xlsx')) {
      toast.error('엑셀 파일(.xlsx)만 업로드 가능합니다.');
      return;
    }

    setIsUploading(true);
    const reader = new FileReader();

    reader.onload = (e: ProgressEvent<FileReader>) => {
      const arrayBuffer = e.target?.result;
      if (arrayBuffer) {
        const data = new Uint8Array(arrayBuffer as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];

        const headers = XLSX.utils.sheet_to_json<string[]>(worksheet, {
          header: 1,
        })[1];

        if (Array.isArray(headers)) {
          const jsonData: { [key: string]: any }[] = XLSX.utils.sheet_to_json(
            worksheet,
            { header: headers, range: 2 }
          );

          const transformedData = mapHeadersToEnglish(jsonData);
          const filteredData = transformedData.map(data => {
            if (data.email === '@') {
              return { ...data, email: null };
            } else {
              return data;
            }
          });

          // 콤마로 구분된 상품코드를 별도의 행으로 확장
          const expandedData: any[] = [];
          let sequenceCounter = 1;

          filteredData.forEach(data => {
            // 쿠폰 명 제거를 위한 대괄호 선 제거
            const cleanedProductCode = data.productCodeOrName
                ? removeBracketContent(data.productCodeOrName)
                : '';
            if (
              !cleanedProductCode ||
              !cleanedProductCode.includes(',')
            ) {
              // 단일 상품코드도 대괄호 제거
              const newData = {
                ...data,
                productCodeOrName: cleanedProductCode,
                sequence: data.sequence
                  ? Number(data.sequence)
                  : sequenceCounter++,
              };
              expandedData.push(newData);
              return;
            }

            const baseSequence = data.sequence
              ? Number(data.sequence)
              : sequenceCounter++;
            const productCodes = data.productCodeOrName
              .split(',')
              .map((code: string) => code.trim())
              .filter(Boolean);

            productCodes.forEach((productCode: string, index: number) => {
              // 대괄호 내용 제거
              const cleanedProductCode = removeBracketContent(productCode);
              expandedData.push({
                ...data,
                productCodeOrName: cleanedProductCode,
                sequence: `${baseSequence}.${index + 1}`,
              });
            });
          });

          setExcelData(expandedData);
          setFiles([file]);
          setIsUploading(false);
        }
      }
    };

    reader.readAsArrayBuffer(file);
  }, []);

  // 파일 업로드 처리
  const handleFileUpload = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;
      processFile(file);
    },
    [processFile]
  );

  // 드래그 앤 드롭 처리
  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) {
        processFile(files[0]);
      }
    },
    [processFile]
  );

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragEnter = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    // 자식 요소로 이동할 때는 무시
    if (e.currentTarget.contains(e.relatedTarget as Node)) {
      return;
    }
    setIsDragging(false);
  }, []);

  // 셀 클릭 (선택)
  const handleCellClick = useCallback((rowIndex: number, field: string) => {
    setSelectedCell({ row: rowIndex, field });
    // 테이블에 포커스를 유지
    if (tableRef.current) {
      tableRef.current.focus();
    }
  }, []);

  // 셀 편집 시작 (더블클릭)
  const handleCellDoubleClick = useCallback(
    (rowIndex: number, field: string) => {
      setEditingCell({ row: rowIndex, field });
      setTempValue(String((excelData[rowIndex] as any)[field] || ''));
    },
    [excelData]
  );

  // 셀 편집 완료
  const handleCellChange = useCallback(
    (rowIndex: number, field: string, value: string) => {
      const updatedData = [...excelData];
      let newValue = value;

      if (field === 'telNo' && newValue) {
        newValue = newValue.replace(/\D/g, '');
        if (newValue.length > 11) {
          newValue = newValue.slice(0, 11);
        }
        if (newValue.length > 3 && newValue.length <= 7) {
          newValue = `${newValue.slice(0, 3)}-${newValue.slice(3)}`;
        } else if (newValue.length > 7) {
          newValue = `${newValue.slice(0, 3)}-${newValue.slice(
            3,
            7
          )}-${newValue.slice(7)}`;
        }
      }

      // 상품코드/상품명 필드일 경우 대괄호 제거
      if (field === 'productCodeOrName' && newValue) {
        newValue = removeBracketContent(newValue);
      }

      (updatedData[rowIndex] as any)[field] = newValue;
      setExcelData(updatedData);
      setEditingCell(null);

      // 편집 완료 후 테이블에 포커스 복원
      setTimeout(() => {
        if (tableRef.current) {
          tableRef.current.focus();
        }
      }, 0);
    },
    [excelData]
  );

  // 선택 토글
  const toggleRowSelection = useCallback(
    (rowIndex: number) => {
      const newSelected = new Set(selectedRows);
      if (newSelected.has(rowIndex)) {
        newSelected.delete(rowIndex);
      } else {
        newSelected.add(rowIndex);
      }
      setSelectedRows(newSelected);
    },
    [selectedRows]
  );

  // 키보드로 셀 이동 및 삭제
  const handleKeyNavigation = useCallback(
    (e: React.KeyboardEvent) => {
      if (!selectedCell) return;

      // 편집 중일 때는 키 네비게이션 무시
      if (editingCell) return;

      const currentColIndex = columns.findIndex(
        col => col?.field === selectedCell.field
      );
      let newRow = selectedCell.row;
      let newColIndex = currentColIndex;

      switch (e.key) {
        case 'ArrowUp':
          e.preventDefault();
          if (selectedCell.row > 0) newRow = selectedCell.row - 1;
          break;
        case 'ArrowDown':
          e.preventDefault();
          if (selectedCell.row < excelData.length - 1)
            newRow = selectedCell.row + 1;
          break;
        case 'ArrowLeft':
          e.preventDefault();
          if (currentColIndex > 0) {
            newColIndex = currentColIndex - 1;
          } else if (selectedCell.row > 0) {
            newRow = selectedCell.row - 1;
            newColIndex = columns.length - 1;
          }
          break;
        case 'ArrowRight':
          e.preventDefault();
          if (currentColIndex < columns.length - 1) {
            newColIndex = currentColIndex + 1;
          } else if (selectedCell.row < excelData.length - 1) {
            newRow = selectedCell.row + 1;
            newColIndex = 0;
          }
          break;
        case 'Enter':
          e.preventDefault();
          if (!e.shiftKey) {
            // Enter: 현재 셀 편집 - 직접 상태 설정
            setEditingCell({
              row: selectedCell.row,
              field: selectedCell.field,
            });
            const currentValue =
              (excelData[selectedCell.row] as any)[selectedCell.field] || '';
            setTempValue(String(currentValue));
          }
          return;
        case 'Delete':
        case 'Backspace':
          e.preventDefault();
          // Delete/Backspace: 선택된 셀 내용 삭제
          handleCellChange(selectedCell.row, selectedCell.field, '');
          return;
        case 'Tab':
          e.preventDefault();
          if (!e.shiftKey) {
            // Tab: 오른쪽으로 이동
            if (currentColIndex < columns.length - 1) {
              newColIndex = currentColIndex + 1;
            } else if (selectedCell.row < excelData.length - 1) {
              newRow = selectedCell.row + 1;
              newColIndex = 0;
            }
          } else {
            // Shift+Tab: 왼쪽으로 이동
            if (currentColIndex > 0) {
              newColIndex = currentColIndex - 1;
            } else if (selectedCell.row > 0) {
              newRow = selectedCell.row - 1;
              newColIndex = columns.length - 1;
            }
          }
          break;
        case ' ':
          // Space: 현재 행 선택 토글
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            toggleRowSelection(selectedCell.row);
            return;
          }
          break;
        default:
          return;
      }

      const nextColumn = columns[newColIndex];
      if (nextColumn) {
        setSelectedCell({ row: newRow, field: nextColumn.field });
      }

      // 선택된 셀로 자동 스크롤
      setTimeout(() => {
        const nextColumn = columns[newColIndex];
        if (!nextColumn) return;
        const cellId = `cell-${newRow}-${nextColumn.field}`;
        const cellElement = document.getElementById(cellId);
        if (cellElement) {
          cellElement.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'nearest',
          });
        }
      }, 0);
    },
    [
      selectedCell,
      editingCell,
      columns,
      excelData,
      handleCellChange,
      toggleRowSelection,
    ]
  );

  // 전체 선택/해제
  const toggleAllSelection = useCallback(() => {
    if (selectedRows.size === excelData.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(excelData.map((_, i) => i)));
    }
  }, [selectedRows, excelData]);

  // 일괄 등록
  const handleBulkSubmit = useCallback(async () => {
    if (editingCell) {
      toast.warn('수정을 완료하고 등록해주세요.');
      return;
    }

    const tempObj = {
      양력: 'SOLAR',
      음력: 'LUNAR',
      '음력(윤달)': 'LUNAR_INTER',
      남성: 'M',
      여성: 'F',
    };

    const tempExcelData = excelData.map((data: IOrderRegistration) => {
      const newObj: any = { ...data, realtimeYn: 'N' };
      const solarType1 = tempObj[data.solarType1];
      newObj.solarType1 = solarType1;
      const sex1 = tempObj[data.sex1];
      newObj.sex1 = sex1;

      if (data.solarType2) {
        const solarType2 = tempObj[data.solarType2];
        newObj.solarType2 = solarType2;
      }
      if (data.sex2) {
        const sex2 = tempObj[data.sex2];
        newObj.sex2 = sex2;
      }
      return newObj;
    });

    postOrder(tempExcelData, navigate);
  }, [editingCell, excelData, navigate, postOrder]);

  // 실시간 등록
  const handleRealtimeSubmit = useCallback(async () => {
    if (editingCell) {
      toast.warn('수정을 완료하고 등록해주세요.');
      return;
    }

    const selectedData = excelData.filter((_, i) => selectedRows.has(i));
    if (selectedData.length === 0) {
      toast.warn('선택된 주문이 없습니다.');
      return;
    }

    const tempObj = {
      양력: 'SOLAR',
      음력: 'LUNAR',
      '음력(윤달)': 'LUNAR_INTER',
      남성: 'M',
      여성: 'F',
    };

    const newSelectedRows = selectedData.map(row => {
      const newObj: any = { ...row, realtimeYn: 'Y' };
      const solarType1 = tempObj[row.solarType1];
      newObj.solarType1 = solarType1;
      const sex1 = tempObj[row.sex1];
      newObj.sex1 = sex1;

      if (row.solarType2) {
        const solarType2 = tempObj[row.solarType2];
        newObj.solarType2 = solarType2;
      }
      if (row.sex2) {
        const sex2 = tempObj[row.sex2];
        newObj.sex2 = sex2;
      }
      return newObj;
    });

    try {
      await postOrder(newSelectedRows);
      setExcelData(prevData => prevData.filter((_, i) => !selectedRows.has(i)));
      setSelectedRows(new Set());

      if (excelData.length === selectedData.length) {
        navigate('/order/list');
      } else {
        setShowRealtimeNotice(true);
      }
    } catch (error) {
      console.error(error);
    }
  }, [editingCell, excelData, selectedRows, navigate, postOrder]);

  // 실시간 등록 알림: 10초 카운트다운 및 자동 닫힘
  useEffect(() => {
    if (!showRealtimeNotice) return;
    setNoticeSecondsLeft(10);
    const intervalId = setInterval(() => {
      setNoticeSecondsLeft(prev => {
        if (prev <= 1) {
          clearInterval(intervalId);
          setShowRealtimeNotice(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => {
      clearInterval(intervalId);
    };
  }, [showRealtimeNotice]);

  return (
    <>
      <style>{`
        .order-table-sticky-header thead,
        .order-table-sticky-header thead * {
          overflow: visible !important;
          background-color: #e5e7eb !important;
          opacity: 1 !important;
        }
        /* Floating toast twinkle and shine effects */
        @keyframes twinkle {
          0%, 100% { box-shadow: 0 12px 24px rgba(239,68,68,0.22); }
          50% { box-shadow: 0 18px 48px rgba(239,68,68,0.42); }
        }
        .twinkle { animation: twinkle 1.4s ease-in-out infinite; }
        @keyframes shine {
          0%, 100% { opacity: 0.9; }
          50% { opacity: 1; }
        }
        .shine { animation: shine 1.1s ease-in-out infinite; }
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          20% { transform: translateX(-2px); }
          40% { transform: translateX(2px); }
          60% { transform: translateX(-1px); }
          80% { transform: translateX(1px); }
        }
        .shake { animation: shake 0.8s ease-in-out infinite; }
      `}</style>
      <div className="!w-full !h-full !flex !flex-col !bg-white !overflow-auto">
        {/* 숨겨진 파일 입력 */}
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx"
          onChange={handleFileUpload}
          className="hidden"
        />

        {/* 메인 테이블 영역 */}
        <div
          className="h-full w-full"
          style={{ paddingTop: 'px', paddingBottom: '60px' }}
        >
          {excelData.length > 0 ? (
            <div
              className="h-full bg-white overflow-auto"
              style={{ backgroundColor: '#f8f8f8' }}
            >
              {/* 파일 정보 표시 - 테이블 상단 sticky */}
              <div className="sticky top-0 z-30 bg-gray-100 border-b border-gray-300 px-4 py-2 flex items-center justify-between">
                <div className="flex items-center text-sm text-gray-600">
                  <span className="font-medium">파일:</span>
                  <span className="ml-2">{files[0]?.name}</span>
                  <span className="ml-4 text-white">|</span>
                  <span className="ml-4">
                    총{' '}
                    <span className="font-semibold text-blue-600">
                      {excelData.length}
                    </span>
                    건
                  </span>
                  {selectedRows.size > 0 && (
                    <>
                      <span className="ml-4 text-gray-400">|</span>
                      <span className="ml-4 text-purple-600">
                        선택{' '}
                        <span className="font-semibold">{selectedRows.size}</span>건
                      </span>
                    </>
                  )}
                  {validationResult && Object.keys(validationResult).length > 0 && (
                    <>
                      <span className="ml-4 text-gray-400">|</span>
                      <span className="ml-4 text-red-600">
                        오류{' '}
                        <span className="font-semibold">
                          {Object.keys(validationResult).length}
                        </span>
                        건
                      </span>
                    </>
                  )}
                </div>
              </div>
              <table
                ref={tableRef}
                className="text-xs select-none order-table-sticky-header"
                style={{
                  minWidth: '100%',
                  borderCollapse: 'separate',
                  borderSpacing: 0,
                  overflow: 'visible',
                }}
                tabIndex={0}
                onKeyDown={handleKeyNavigation}
              >
                <thead
                  className="sticky top-0 z-20"
                  style={{
                    position: 'sticky',
                    top: 0,
                    backgroundColor: '#e5e7eb',
                    overflow: 'visible',
                  }}
                >
                  <tr style={{ backgroundColor: '#e5e7eb' }}>
                    <th
                      className="border-t border-l border-r border-b-2 border-gray-400 bg-gray-200 p-0 w-12"
                      style={{
                        borderBottomColor: '#666',
                        backgroundColor: '#e5e7eb',
                        overflow: 'visible',
                      }}
                    >
                      <label
                        className="flex items-center justify-center h-full w-full p-2 cursor-pointer hover:bg-gray-300 transition-colors"
                        style={{ backgroundColor: '#e5e7eb' }}
                      >
                        <input
                          type="checkbox"
                          checked={
                            selectedRows.size === excelData.length &&
                            excelData.length > 0
                          }
                          onChange={toggleAllSelection}
                          className="w-4 h-4 cursor-pointer text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                        />
                      </label>
                    </th>
                    {columns.map((col, index) => (
                      col && <th
                        key={col.field}
                        className="border-t border-r border-b-2 border-gray-400 bg-gray-200 px-2 py-1 font-medium text-left whitespace-nowrap select-none"
                        style={{
                          width: col.width,
                          minWidth: col.width,
                          borderBottomColor: '#666',
                          borderLeftWidth: index === 0 ? '1px' : '0',
                          backgroundColor: '#e5e7eb',
                          overflow: 'visible',
                        }}
                      >
                        {col.header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody style={{ overflow: 'visible' }}>
                  {excelData.map((row, rowIndex) => (
                    <tr
                      key={rowIndex}
                      className={`${
                        selectedRows.has(rowIndex)
                          ? 'bg-blue-100'
                          : 'even:bg-gray-50 odd:bg-white hover:bg-yellow-50'
                      } transition-colors`}
                    >
                      <td className="border-l border-r border-b border-gray-300 p-0 text-center bg-gray-50">
                        <label className="flex items-center justify-center h-full w-full p-2 cursor-pointer hover:bg-gray-200 transition-colors">
                          <input
                            type="checkbox"
                            checked={selectedRows.has(rowIndex)}
                            onChange={() => toggleRowSelection(rowIndex)}
                            className="w-4 h-4 cursor-pointer text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                          />
                        </label>
                      </td>
                      {columns.map(col => {
                        if (!col) return null;
                        const isEditing =
                          editingCell?.row === rowIndex &&
                          editingCell?.field === col.field;
                        const isSelected =
                          selectedCell?.row === rowIndex &&
                          selectedCell?.field === col.field;
                        const hasError =
                          validationResult?.[rowIndex]?.[col.field];
                        const cellValue = (row as any)[col.field] ?? '';

                        return (
                          <td
                            key={col.field}
                            id={`cell-${rowIndex}-${col.field}`}
                            className={`border-r border-b border-gray-300 px-2 py-1 cursor-cell relative group align-middle ${
                              hasError
                                ? 'bg-red-100 text-red-700 font-medium'
                                : ''
                            } ${
                              isEditing
                                ? 'ring-2 ring-blue-500 ring-inset z-10'
                                : ''
                            } ${
                              isSelected && !isEditing
                                ? 'ring-2 ring-blue-400 ring-inset bg-blue-50'
                                : ''
                            }`}
                            onClick={() => handleCellClick(rowIndex, col.field)}
                            onDoubleClick={() =>
                              !isEditing &&
                              handleCellDoubleClick(rowIndex, col.field)
                            }
                            title={hasError || ''}
                            style={{
                              minHeight: '28px',
                              borderLeftWidth:
                                columns.indexOf(col) === 0 ? '1px' : '0',
                            }}
                          >
                            {isEditing ? (
                              <input
                                type="text"
                                value={tempValue}
                                onChange={e => setTempValue(e.target.value)}
                                onBlur={() =>
                                  handleCellChange(
                                    rowIndex,
                                    col.field,
                                    tempValue
                                  )
                                }
                                onKeyDown={e => {
                                  e.stopPropagation(); // 테이블의 키 이벤트와 충돌 방지
                                  // IME 조합 중이면 무시 (한글 입력 시 마지막 글자 잘림 방지)
                                  if (e.nativeEvent.isComposing) return;
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleCellChange(
                                      rowIndex,
                                      col.field,
                                      tempValue
                                    );
                                    // Enter 후 아래 셀로 이동
                                    setTimeout(() => {
                                      let nextRow = rowIndex;
                                      if (rowIndex < excelData.length - 1) {
                                        nextRow = rowIndex + 1;
                                        setSelectedCell({
                                          row: nextRow,
                                          field: col.field,
                                        });
                                      } else {
                                        setSelectedCell({
                                          row: rowIndex,
                                          field: col.field,
                                        });
                                      }
                                      // 테이블에 포커스 복원
                                      if (tableRef.current) {
                                        tableRef.current.focus();
                                      }
                                      // 다음 셀로 자동 스크롤
                                      const nextCellId = `cell-${nextRow}-${col.field}`;
                                      const nextCellElement =
                                        document.getElementById(nextCellId);
                                      if (nextCellElement) {
                                        nextCellElement.scrollIntoView({
                                          behavior: 'smooth',
                                          block: 'nearest',
                                          inline: 'nearest',
                                        });
                                      }
                                    }, 50); // 약간의 지연을 추가하여 상태 업데이트가 완료되도록 함
                                  } else if (e.key === 'Tab') {
                                    e.preventDefault();
                                    handleCellChange(
                                      rowIndex,
                                      col.field,
                                      tempValue
                                    );
                                    // Tab 후 다음 셀로 이동
                                    setTimeout(() => {
                                      const currentColIndex = columns.findIndex(
                                        c => c?.field === col.field
                                      );
                                      let nextRow = rowIndex;
                                      let nextField = col.field;

                                      if (
                                        currentColIndex <
                                        columns.length - 1
                                      ) {
                                        const nextCol = columns[currentColIndex + 1];
                                        if (nextCol) {
                                          nextField = nextCol.field;
                                        }
                                        setSelectedCell({
                                          row: rowIndex,
                                          field: nextField,
                                        });
                                      } else if (
                                        rowIndex <
                                        excelData.length - 1
                                      ) {
                                        nextRow = rowIndex + 1;
                                        const firstCol = columns[0];
                                        if (firstCol) {
                                          nextField = firstCol.field;
                                        }
                                        setSelectedCell({
                                          row: nextRow,
                                          field: nextField,
                                        });
                                      }
                                      // 테이블에 포커스 복원
                                      if (tableRef.current) {
                                        tableRef.current.focus();
                                      }
                                      // 다음 셀로 자동 스크롤
                                      const nextCellId = `cell-${nextRow}-${nextField}`;
                                      const nextCellElement =
                                        document.getElementById(nextCellId);
                                      if (nextCellElement) {
                                        nextCellElement.scrollIntoView({
                                          behavior: 'smooth',
                                          block: 'nearest',
                                          inline: 'nearest',
                                        });
                                      }
                                    }, 50);
                                  } else if (e.key === 'Escape') {
                                    e.preventDefault();
                                    setEditingCell(null);
                                    setSelectedCell({
                                      row: rowIndex,
                                      field: col.field,
                                    });
                                    // 테이블에 포커스 복원
                                    setTimeout(() => {
                                      if (tableRef.current) {
                                        tableRef.current.focus();
                                      }
                                    }, 0);
                                  }
                                }}
                                className="w-full h-full px-1 py-0.5 -mx-1 border-none outline-none bg-white text-xs"
                                style={{
                                  // 한자가 포함된 경우 폰트 패밀리 추가
                                  fontFamily:
                                    /[\u4E00-\u9FFF\u3400-\u4DBF\uF900-\uFAFF]/.test(
                                      tempValue
                                    )
                                      ? '"Noto Sans CJK KR", "Noto Sans KR", "Malgun Gothic", "맑은 고딕", sans-serif'
                                      : 'inherit',
                                }}
                                autoFocus
                              />
                            ) : (
                              <div className="truncate relative flex items-center h-full">
                                <span
                                  className="relative z-10"
                                  style={{
                                    // 한자가 포함된 경우 폰트 패밀리 추가
                                    fontFamily:
                                      /[\u4E00-\u9FFF\u3400-\u4DBF\uF900-\uFAFF]/.test(
                                        cellValue
                                      )
                                        ? '"Noto Sans CJK KR", "Noto Sans KR", "Malgun Gothic", "맑은 고딕", sans-serif'
                                        : 'inherit',
                                  }}
                                >
                                  {cellValue}
                                </span>
                                {!hasError && !isSelected && (
                                  <span className="absolute inset-0 bg-blue-100 opacity-0 group-hover:opacity-20 pointer-events-none transition-opacity" />
                                )}
                                {hasError && (
                                  <div className="absolute -top-8 left-0 bg-red-600 text-white text-xs px-2 py-1 rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-30">
                                    {hasError}
                                  </div>
                                )}
                              </div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="min-h-full bg-white flex flex-col md:flex-row items-start md:items-stretch justify-start md:justify-center py-6 md:py-0 md:pt-16 px-4 md:px-0 overflow-y-auto">
              {/* Step 1 - 설명 + 샘플 양식 큰 버튼 */}
              <div className="w-full md:w-1/2 md:border-r border-b md:border-b-0 border-dashed border-gray-300 flex items-start justify-center p-6 md:p-16">
                <div className="max-w-2xl w-full space-y-4 md:space-y-8">
                  <div className="space-y-3 md:space-y-4">
                    <div className="flex items-center">
                      <span className="flex items-center justify-center w-8 h-8 md:w-10 md:h-10 rounded-full bg-blue-50 text-blue-700 font-bold ring-1 ring-blue-200 mr-3 flex-shrink-0">
                        1
                      </span>
                      <div className="text-lg md:text-3xl font-extrabold text-gray-900 tracking-tight leading-tight">
                        주문서 양식을 다운로드 받으세요
                      </div>
                    </div>
                    <div
                      className="text-sm md:text-xl text-gray-800 leading-6 md:leading-8 space-y-2"
                      style={{ textWrap: 'pretty' }}
                    >
                      <p className="font-medium">
                        주문서 양식 다운로드 버튼을 클릭하여 주문서 양식을
                        다운로드&nbsp;받아&nbsp;주세요.
                      </p>
                      <p>
                        상품명 또는 상품코드와 필수 고객 정보를 정확히
                        입력해&nbsp;주세요.
                      </p>
                      <p>
                        정확히 입력하셨다면 우측의 주문서 업로드를
                        진행해&nbsp;주세요.
                      </p>
                    </div>
                  </div>
                  <a
                    href={isBizProfileEnabled ? "/주문등록_양식_biz.xlsx": "/주문등록_양식.xlsx"}
                    download
                    className="w-full flex items-center justify-center px-6 py-4 md:px-10 md:py-8 text-base md:text-2xl bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl md:rounded-2xl shadow-md border border-transparent transition-colors"
                  >
                    <FontAwesomeIcon
                      icon={faDownload}
                      className="mr-3 md:mr-4 text-white"
                    />
                    <span className="font-bold">주문서 양식 다운로드</span>
                  </a>
                </div>
              </div>

              {/* Step 2 - 큰 드래그앤드롭 박스 + 큰 업로드 버튼 */}
              <div className="w-full md:w-1/2 p-6 md:p-16 flex items-start justify-center">
                <div className="max-w-2xl w-full">
                  <div className="flex items-center mb-4 md:mb-5">
                    <span className="flex items-center justify-center w-8 h-8 md:w-10 md:h-10 rounded-full bg-blue-50 text-blue-700 font-bold ring-1 ring-blue-200 mr-3 flex-shrink-0">
                      2
                    </span>
                    <div className="text-lg md:text-3xl font-extrabold text-gray-900 tracking-tight leading-tight">
                      주문서를 업로드 해주세요
                    </div>
                  </div>
                  <div
                    className={`w-full min-h-[250px] md:min-h-[400px] rounded-xl md:rounded-2xl border-2 border-dashed transition-all flex flex-col items-center justify-center text-center px-6 py-6 md:px-10 md:py-10 ${
                      isDragging
                        ? 'border-blue-400 bg-blue-50'
                        : 'border-gray-300 bg-gray-50'
                    }`}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragEnter={handleDragEnter}
                    onDragLeave={handleDragLeave}
                  >
                    <FontAwesomeIcon
                      icon={faUpload}
                      className={`mb-3 md:mb-6 transition-colors text-4xl md:text-7xl ${
                        isDragging ? 'text-blue-400' : 'text-gray-300'
                      }`}
                    />
                    <div
                      className={`text-base md:text-2xl font-semibold mb-2 md:mb-3 ${
                        isDragging ? 'text-blue-700' : 'text-gray-700'
                      }`}
                    >
                      주문서를 여기에 드래그하세요
                    </div>
                    <p className="text-xs md:text-base text-gray-500 mb-4 md:mb-7">
                      또는 아래 버튼을 눌러 파일을 선택하세요
                    </p>
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="pointer-events-auto inline-flex items-center justify-center px-6 py-3 md:px-12 md:py-6 text-base md:text-2xl bg-blue-600 hover:bg-blue-700 text-white rounded-xl md:rounded-2xl shadow-md transition-colors"
                    >
                      <FontAwesomeIcon icon={faUpload} className="mr-2 md:mr-4" />
                      주문서 업로드
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* 실시간 등록 후 잔여 행이 있을 때 큰 하단 배너 안내 */}
      {showRealtimeNotice && (
        <div className="fixed left-1/2 -translate-x-1/2 bottom-12 z-50 w-[calc(100%-24px)] sm:w-auto px-3">
          <div className="mx-auto max-w-3xl bg-red-50 rounded-3xl shadow-2xl ring-1 ring-red-200 px-8 py-8 md:px-10 md:py-10 twinkle shake transform scale-150 origin-bottom">
            <div className="flex items-start justify-between gap-6">
              <div className="flex items-start gap-4">
                <FontAwesomeIcon
                  icon={faExclamationCircle}
                  className="text-red-600 text-4xl md:text-5xl mt-0.5"
                />
                <div className="text-red-900" style={{ textWrap: 'pretty' }}>
                  <div className="text-xl md:text-3xl font-extrabold leading-snug">
                    주문대기 페이지에서 ‘주문확정’을 해야 전송됩니다.
                  </div>
                  <div className="text-lg md:text-xl mt-3 leading-relaxed">
                    일부만 실시간 등록하셨습니다. 남은 주문은 확인 후 주문대기
                    페이지에서 ‘주문확정’을 눌러 주세요.
                  </div>
                  <div className="text-base md:text-lg text-red-700 mt-4">
                    자동으로 닫힘: {noticeSecondsLeft}초
                  </div>
                </div>
              </div>
              <button
                onClick={() => setShowRealtimeNotice(false)}
                className="shrink-0 inline-flex items-center px-6 md:px-8 py-4 md:py-5 rounded-2xl border border-red-300 text-red-800 bg-red-50 hover:bg-red-200 text-lg md:text-xl"
              >
                닫기
              </button>
            </div>
            <div className="mt-5 h-3 bg-red-100 rounded overflow-hidden">
              <div
                className="h-full bg-red-500 transition-all duration-300 shine"
                style={{ width: `${(noticeSecondsLeft / 10) * 100}%` }}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default OrderRegistration;
